import {
  UPDATE_FILTERS,
  UPDATE_SEARCH_RESULTS,
  SET_SORT_BY,
  SET_FILTERS,
  // SET_SEARCH_PARAMS,
} from './types'
import _ from 'lodash'
import {
  // filterDuplicateResults,
  updateOpeningTimesFilter,
} from 'settings/app/helpers'
import { buildUrl } from 'settings/app/urlHelpers'
import navigatorAPI from 'settings/xhr/navigatorAPI'
import {
  entryPointId,
  MUTUALLY_EXCLUSIVE_FILTERS,
} from 'settings/app/constants'
import { getQsrIdentifierArray } from './compareActions'
import { setTypeSpecificSearchParams } from './searchActions'

/**
 * receive a filter collection and update one filter
 * notice (only boolean based filters)
 * @param array
 * @param action
 * @returns {*}
 */
export function updateFilterInCollection(array, action) {
  return array.map((item) => {
    if (item.key === action.parentKey) {
      return {
        ...item,
        options: updateFilterInCollection(item.options, { key: action.key }),
      }
    }

    // mutually exclusive filters
    // condition: filters must be eather parent or in the same group
    // https://bugs.onedev.aok.de/browse/MGMNAVSUP-333
    if (item.key === MUTUALLY_EXCLUSIVE_FILTERS[action.key]) {
      return {
        ...item,
        selected: false,
      }
    }

    if (item.key !== action.key) {
      return item
    }

    return {
      ...item,
      selected: !item.selected,
    }
  })
}

/**
 * reset ui change
 * @param array
 * @returns {*}
 */
export function resetFilterInCollection(array) {
  // eslint-disable-next-line array-callback-return
  return array.map((item) => {
    if (item.options && 'object' === typeof item.options) {
      if (item.type === 'modal') {
        return {
          ...item,
          tabs: item.tabs.map((tab) => ({
            ...tab,
            options: tab.options.map((option) => ({
              ...option,
              options: option.options.map((subOption) => ({
                ...subOption,
                selected: false,
              })),
            })),
          })),
        }
      }

      return {
        ...item,
        options: Object.keys(item.options).map((index) => {
          const { key } = item.options[index]
          return {
            ...item.options[index],
            selected:
              key === 'opening_day' || key === 'opening_time' ? '' : false,
          }
        }),
      }
    } else if (item.options) {
      return {
        ...item,
        options: resetFilterInCollection(item.options),
      }
    }

    if ('boolean' === item.type) {
      return {
        ...item,
        selected: false,
      }
    }
  })
}

/**
 * handle filter selection
 * Single && Grouped Filters =>  high_care_quality=true
 *
 * workflow
 * 1. get current filter collection and filter (which should set/unset)
 *  update this filter collection @ updateOpeningTimesFilter/updateFilterInCollection
 *
 *  2. update redux filter collection
 *  3  if filter collection has a removed filter that equals the search params reset search completely
 *  4. api call
 *  5. update results/filter
 * @param action
 * @param filterObject
 * @param callback
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const setFilterSelection =
  (action, filterObject, callback) => async (dispatch, getState) => {
    let { collection, activeSort } = getState().filter
    const { search } = getState()
    const currentEntryPoint = getState().application.entryPoint

    // set time filter to user selection (time filters contains no boolean)
    if ('custom_opening_hours' === filterObject.filter) {
      collection = updateOpeningTimesFilter(collection, filterObject)
    }

    // only one filter could change (updateFilter sets only booleans)
    if (
      'desktopFilter' === action &&
      'custom_opening_hours' !== filterObject.filter
    ) {
      collection = updateFilterInCollection(collection, {
        key: filterObject.filter,
        parentKey: filterObject.parentKey,
      })
    }

    // get mobile filters
    if ('mobileFilter' === action) {
      collection = filterObject.mobileFilters
      activeSort = filterObject.mobileSort
    }

    // modal filters (new structure)
    if ('modalFilter' === action) {
      // set updated filter object based on key
      // hmmmmm
      collection = collection.map((filter) => {
        if (filter.key === filterObject.indicatorFilters.key) {
          return filterObject.indicatorFilters
        }
        return filter
      })
    }

    // update store immediately
    if ('desktopFilter' === action) {
      dispatch({
        type: UPDATE_FILTERS,
        payload: {
          collection,
          activeSort,
        },
      })
    }

    // if a filter was removed which corresponds to the current search params
    // the search should be reset completely
    if (
      collection.findIndex(
        (collection) =>
          collection.key === search.prevParams.type &&
          collection.options.findIndex(
            (option) =>
              option.key.includes(search.prevParams.key) &&
              option.selected === false
          ) > -1
      ) > -1
    ) {
      dispatch(setTypeSpecificSearchParams(currentEntryPoint))
    }

    const [apiUrl, getUrl] = buildUrl(getState(), { collection })

    const { data } = await navigatorAPI.get(apiUrl)
    const { results, count } = data

    // get qsr identifier index
    const qsrIdentifier =
      currentEntryPoint === entryPointId.HOSPITALS
        ? getQsrIdentifierArray(results)
        : []

    dispatch({
      type: UPDATE_SEARCH_RESULTS,
      payload: {
        results,
        count,
        noResults: data.results.length === 0,
        isTreatmentReason: data.is_treatment_reason,
        showDetails: data.show_details,
        services: data.aokServices,
        qsrIdentifier,
      },
    })

    dispatch({
      type: UPDATE_FILTERS,
      payload: {
        collection: data.filters,
        activeSort: data.active_sorting,
      },
    })

    callback(getUrl)
  }

/**
 * handle sorting selection
 * will fired when user changed the result sorting
 * @param sorting
 * @param callback
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const setSortingSelection =
  (sorting, callback) => async (dispatch, getState) => {
    const [apiUrl, getUrl] = buildUrl(getState(), { sorting })
    const currentEntryPoint = getState().application.entryPoint

    // update store immediately
    dispatch({
      type: SET_SORT_BY,
      payload: sorting,
    })

    const { data } = await navigatorAPI.get(apiUrl)
    const { results, count } = data

    // get qsr identifier index
    const qsrIdentifier =
      currentEntryPoint === entryPointId.HOSPITALS
        ? getQsrIdentifierArray(results)
        : []

    // hide duplicate results for caresupport (also happens in searchAction.js)
    // if(currentEntryPoint === entryPointId.CARESUPPORT) {
    //   results = filterDuplicateResults(results)
    //   if(data.results.length - results.length > 0) {
    //     count = count - (data.results.length - results.length)
    //     console.error(`Duplicate results found for caresupport! Duplicates that were removed:`, _.difference(data.results, results))
    //   }
    // }

    dispatch({
      type: UPDATE_SEARCH_RESULTS,
      payload: {
        results,
        count,
        noResults: data.results.length === 0,
        isTreatmentReason: data.is_treatment_reason,
        qsrIdentifier,
      },
    })

    dispatch({
      type: SET_SORT_BY,
      payload: data.active_sorting,
    })

    callback(getUrl)
  }

/**
 * reset all filters
 * @param callback
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const resetFilterCollection =
  (callback) => async (dispatch, getState) => {
    let { collection, activeSort } = getState().filter
    const currentEntryPoint = getState().application.entryPoint

    collection = resetFilterInCollection(collection)

    // update store immediately
    dispatch({
      type: UPDATE_FILTERS,
      payload: {
        collection,
        activeSort,
      },
    })

    const [apiUrl, getUrl] = buildUrl(getState(), { collection })

    const { data } = await navigatorAPI.get(apiUrl)

    // get qsr identifier index
    const qsrIdentifier =
      currentEntryPoint === entryPointId.HOSPITALS
        ? getQsrIdentifierArray(data.results)
        : []

    dispatch({
      type: UPDATE_SEARCH_RESULTS,
      payload: {
        results: data.results,
        count: data.count,
        noResults: data.results.length === 0,
        isTreatmentReason: data.is_treatment_reason,
        qsrIdentifier,
      },
    })

    dispatch({
      type: SET_FILTERS,
      payload: {
        collection: data.filters,
        sorting: data.sorting,
        activeSort: data.active_sorting,
      },
    })

    callback(getUrl)
  }
